import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Check, X, Tag, Lock } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ColorPicker } from "@/components/ui/color-picker";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const PROTECTED_TAG_NAMES = new Set(["call pending"]);
const isProtectedTagName = (name: string): boolean =>
  PROTECTED_TAG_NAMES.has(name.trim().toLowerCase());

export function SettingsOpportunityTagsPage() {
  const utils = trpc.useUtils();
  const list = trpc.opportunityTags.list.useQuery();

  const [name, setName] = useState("");
  const [color, setColor] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState<string | null>(null);

  const create = trpc.opportunityTags.create.useMutation({
    onSuccess: () => {
      utils.opportunityTags.list.invalidate();
      setName("");
      setColor(null);
      toast.success("Tag added");
    },
    onError: (err) => toast.error(err.message || "Failed to add"),
  });

  const update = trpc.opportunityTags.update.useMutation({
    onSuccess: () => {
      utils.opportunityTags.list.invalidate();
      setEditingId(null);
      toast.success("Updated");
    },
    onError: (err) => toast.error(err.message || "Failed to update"),
  });

  const remove = trpc.opportunityTags.delete.useMutation({
    onSuccess: () => {
      utils.opportunityTags.list.invalidate();
      toast.success("Removed");
    },
    onError: () => toast.error("Failed to remove"),
  });

  const items = list.data ?? [];

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) return;
    create.mutate({ name: n, color });
  };

  const startEdit = (id: string, currentName: string, currentColor: string | null) => {
    setEditingId(id);
    setEditName(currentName);
    setEditColor(currentColor);
  };

  const saveEdit = (id: string) => {
    const n = editName.trim();
    if (!n) return;
    update.mutate({
      id,
      patch: { name: n, color: editColor },
    });
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 md:px-6">
      <div className="mb-6 flex items-center gap-3">
        <Tag className="h-6 w-6 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Opportunity tags</h1>
          <p className="text-sm text-muted-foreground">
            Tags that can be applied to opportunities. Manage the list available across the app.
          </p>
        </div>
      </div>

      <form onSubmit={handleAdd} className="mb-6 flex flex-wrap items-center gap-2">
        <Input
          placeholder="Tag name (e.g. Approach needed)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="min-w-[220px] flex-1"
          maxLength={64}
          required
        />
        <ColorPicker value={color} onChange={setColor} ariaLabel="Pick tag color" />
        <Button type="submit" disabled={create.isPending || !name.trim()}>
          <Plus className="mr-2 h-4 w-4" />
          Add
        </Button>
      </form>

      {list.isLoading ? (
        <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed py-12 text-center text-sm text-muted-foreground">
          No tags yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <Table className="min-w-[480px]">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="w-32">Color</TableHead>
                <TableHead className="w-28" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((t) => {
                const isEditing = editingId === t.id;
                const isLocked = isProtectedTagName(t.name);
                return (
                  <TableRow key={t.id} className="align-top">
                    <TableCell className="font-medium">
                      {isEditing ? (
                        <Input
                          autoFocus
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          maxLength={64}
                          className="h-8"
                        />
                      ) : (
                        <span className="inline-flex items-center gap-2">
                          <span
                            className="inline-block h-3 w-3 shrink-0 rounded-full border"
                            style={{ backgroundColor: t.color ?? "transparent" }}
                            aria-hidden
                          />
                          {t.name}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {isEditing ? (
                        <ColorPicker
                          value={editColor}
                          onChange={setEditColor}
                          ariaLabel="Pick tag color"
                        />
                      ) : (
                        <span className="font-mono text-xs">{t.color ?? "—"}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        {isEditing ? (
                          <>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              disabled={update.isPending}
                              onClick={() => saveEdit(t.id)}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              onClick={() => setEditingId(null)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-muted-foreground"
                              onClick={() => startEdit(t.id, t.name, t.color)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            {isLocked ? (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex">
                                      <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-8 w-8 text-muted-foreground"
                                        disabled
                                        aria-label="Locked from deletion"
                                      >
                                        <Lock className="h-4 w-4" />
                                      </Button>
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    This tag is locked and cannot be deleted.
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                    disabled={remove.isPending}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Remove?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      <span className="font-medium">{t.name}</span> will be removed
                                      from all opportunities it was applied to.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                      onClick={() => remove.mutate({ id: t.id })}
                                    >
                                      Remove
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
